/// <reference types="@types/google.maps" />
import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { useGoogleMapsApi } from '@/hooks/useGoogleMapsApi';
import { formatPriceDisplay } from '@/utils/currency';

interface Property {
  id: string;
  title: string;
  location: string;
  latitude?: number;
  longitude?: number;
  price: number;
  property_type: string;
  address?: string;
  city?: string;
  pincode?: string;
}

interface PropertyMapProps {
  properties?: Property[];
  center?: [number, number];
  zoom?: number;
  height?: string;
  showControls?: boolean;
  onPropertyClick?: (property: Property) => void;
}

const PropertyMap: React.FC<PropertyMapProps> = ({
  properties = [],
  center = [72.8777, 19.0760], // Mumbai default
  zoom = 12,
  height = '400px',
  showControls = true,
  onPropertyClick
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.Marker[]>([]);
  const { apiKey: googleMapsApiKey, isLoading, error } = useGoogleMapsApi();


  const initializeMap = async (apiKey: string) => {
    if (!mapContainer.current || map.current) return;

    try {
      const { Loader } = await import('@googlemaps/js-api-loader');
      const loader = new Loader({
        apiKey: apiKey,
        version: 'weekly',
        libraries: ['places']
      });

      await loader.load();

      map.current = new google.maps.Map(mapContainer.current, {
        center: { lat: center[1], lng: center[0] },
        zoom: zoom,
        disableDefaultUI: !showControls,
        zoomControl: showControls,
        streetViewControl: showControls,
        fullscreenControl: showControls
      });

      // Add properties as markers
      addMarkersToMap();
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  const createCustomMarkerIcon = () => {
    // Simple, clean marker using just your monogram
    return {
      url: '/lovable-uploads/66876f51-ee47-4451-91b2-83f670e1517c.webp',
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 20),
      origin: new google.maps.Point(0, 0)
    };
  };

  const geocodeAndAddMarker = async (property: Property) => {
    if (!map.current) return;
    
    let position: google.maps.LatLngLiteral | null = null;
    
    if (property.latitude && property.longitude) {
      position = { lat: property.latitude, lng: property.longitude };
    } else {
      // Geocode using address
      const addressParts = [property.address, property.location, property.city, property.pincode, 'India'].filter(Boolean);
      const fullAddress = addressParts.join(', ');
      if (!fullAddress || fullAddress === 'India') return;
      
      try {
        const geocoder = new google.maps.Geocoder();
        const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode({ address: fullAddress }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              resolve(results);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          });
        });
        position = {
          lat: result[0].geometry.location.lat(),
          lng: result[0].geometry.location.lng()
        };
      } catch (err) {
        console.warn(`Could not geocode property "${property.title}":`, err);
        return;
      }
    }
    
    if (!position || !map.current) return;

    // Center map on this property if it's the only one
    if (properties.length === 1) {
      map.current.setCenter(position);
      map.current.setZoom(15);
    }

    const marker = new google.maps.Marker({
      position,
      map: map.current,
      title: property.title,
      icon: createCustomMarkerIcon()
    });

    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="p-4 min-w-[250px]">
          <div class="flex items-start gap-2 mb-3">
            <div class="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span class="text-xs font-bold text-blue-600">M</span>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-base mb-1 text-gray-900">${property.title}</h3>
              <p class="text-sm text-gray-600 mb-2">${property.location}</p>
            </div>
          </div>
          <div class="space-y-2">
            <div class="flex justify-between items-center py-1">
              <span class="text-sm text-gray-600">Price:</span>
              <span class="font-bold text-green-600 text-base">${formatPriceDisplay({ price: property.price })}</span>
            </div>
            <div class="flex justify-between items-center py-1">
              <span class="text-sm text-gray-600">Type:</span>
              <span class="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded capitalize">${property.property_type}</span>
            </div>
          </div>
          <div class="mt-3 pt-2 border-t border-gray-200">
            <p class="text-xs text-gray-500 text-center">Click to view details</p>
          </div>
        </div>
      `
    });

    marker.addListener('mouseover', () => {
      infoWindow.open(map.current, marker);
    });

    marker.addListener('mouseout', () => {
      setTimeout(() => infoWindow.close(), 200);
    });

    marker.addListener('click', () => {
      infoWindow.open(map.current, marker);
      if (onPropertyClick) onPropertyClick(property);
    });

    markers.current.push(marker);
  };

  const addMarkersToMap = async () => {
    if (!map.current) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.setMap(null));
    markers.current = [];

    for (const property of properties) {
      await geocodeAndAddMarker(property);
    }
  };

  useEffect(() => {
    if (googleMapsApiKey) {
      initializeMap(googleMapsApiKey);
    }

    return () => {
      // Cleanup markers
      markers.current.forEach(marker => marker.setMap(null));
      markers.current = [];
      map.current = null;
    };
  }, [googleMapsApiKey]);

  // Update markers when properties change
  useEffect(() => {
    if (map.current && googleMapsApiKey) {
      addMarkersToMap();
    }
  }, [properties, googleMapsApiKey]);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto">
            <MapPin className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Loading Map</h3>
            <p className="text-muted-foreground">
              Initializing Google Maps...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (error || !googleMapsApiKey) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto">
            <MapPin className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Map Unavailable</h3>
            <p className="text-muted-foreground">
              {error || 'Google Maps integration is currently unavailable. Please try again later.'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div 
        ref={mapContainer} 
        style={{ height }} 
        className="w-full"
      />
    </Card>
  );
};

export default PropertyMap;